import { type AccessPolicy, canEditWithStatus, getWorkflowPermissionsByStatus } from '@takeshape/actions';
import {
  defaultWorkflow,
  findWorkflowStep,
  LEGACY_API_VERSION,
  type ShapeJSON,
  type Workflow,
  type WorkflowStep
} from '@takeshape/schema';
import { getApiVersion } from 'app/branches/selectors';
import type { Suggestion } from 'app/common/utils/autocomplete';
import { FORM_NAME } from 'app/content/constants';
import { selectAccessPolicy } from 'app/permissions/selectors';
import { getCurrentProjectId } from 'app/project/selectors';
import { getInitialData } from 'app/schema-form/selectors';
import { fromJS } from 'immutable';
import orderBy from 'lodash/orderBy';
import { createSelector } from 'reselect';
import type { AppState } from '../app-state.js';
import { getSchema, getShapeById, getShapeByName } from '../schema/index.js';
import { getRouter } from '../selectors.js';
import { STATUS_PATH } from './constants.js';

export const getWorkflowId = createSelector(getRouter, (router) => router.params?.workflowId ?? '');

export const getWorkflowList = createSelector(getSchema, (schema) =>
  schema.workflows ? orderBy(Object.values(schema.workflows), ['title']) : []
);

export const getCustomWorkflowCount = createSelector(
  getWorkflowList,
  (workflows) => workflows.filter((workflow) => workflow.name !== defaultWorkflow.name).length
);

export const getWorkflowById = createSelector(
  getSchema,
  getWorkflowId,
  (schema, workflowId) => schema.workflows[workflowId]
);

export function getWorkflowStepOptions(workflow: Workflow, label?: string): Suggestion[] {
  return workflow.steps.map((s) => ({
    label: `${label ?? workflow.title}: ${s.title}`,
    value: s.key
  }));
}

export const getWorkflowOptions = createSelector(getWorkflowList, (workflows: Workflow[]) =>
  fromJS(workflows.flatMap((w) => getWorkflowStepOptions(w)))
);

export const getStepKey = (_state: AppState, props: { stepKey: string }): string => props.stepKey;

export const getStepByKeyOrNameFromWorkflowList = (
  workflows: Workflow[],
  stepKeyOrName: string
): WorkflowStep | undefined => {
  return workflows
    .flatMap((w: Workflow) => w.steps)
    .find((s: WorkflowStep) => s.key === stepKeyOrName || s.name === stepKeyOrName);
};

export const getWorkflowStepByKey = createSelector(getWorkflowList, getStepKey, (workflows: Workflow[], stepKey) =>
  getStepByKeyOrNameFromWorkflowList(workflows, stepKey)
);

const getShapeWorkflow = (shape: ShapeJSON | undefined, workflowList: Workflow[]): Workflow => {
  const workflow = shape?.workflow ?? 'default';
  return workflowList.find((w: Workflow) => w.name === workflow) ?? defaultWorkflow;
};

export const getWorkflowByShapeId = createSelector(getShapeById, getWorkflowList, (shape, workflowList) => {
  return getShapeWorkflow(shape, workflowList);
});

export const getWorkflowByShapeName = createSelector(getShapeByName, getWorkflowList, (shape, workflowList) => {
  return getShapeWorkflow(shape, workflowList);
});

export const getCurrentWorkflowStatus = (state: unknown): string =>
  getInitialData(state, { formName: FORM_NAME, path: STATUS_PATH }) as string;

export const getWorkflowStepPermissions = createSelector(
  selectAccessPolicy,
  getCurrentProjectId,
  getWorkflowByShapeName,
  getCurrentWorkflowStatus,
  (accessPolicy: AccessPolicy, projectId: string, workflow: Workflow, status: string) => {
    const context = {
      projectId,
      accessPolicy,
      workflow
    };
    return getWorkflowPermissionsByStatus(context, status);
  }
);

export const canEditByWorkflowStatus = createSelector(
  getApiVersion,
  selectAccessPolicy,
  getCurrentProjectId,
  getWorkflowByShapeName,
  getCurrentWorkflowStatus,

  (apiVersion: string, accessPolicy: AccessPolicy, projectId: string, workflow: Workflow, status: string) => {
    if (apiVersion === LEGACY_API_VERSION) {
      return true;
    }

    const context = {
      projectId,
      accessPolicy,
      workflow
    };
    if (!status) {
      return false;
    }

    const statusStep = findWorkflowStep(workflow, status);
    if (!statusStep) {
      return false;
    }

    return canEditWithStatus(context, statusStep.step.key);
  }
);

export const canScheduleWorkflowStatus = createSelector(
  getWorkflowByShapeName,
  getCurrentWorkflowStatus,
  (workflow: Workflow, status: string) => {
    return workflow.steps.map((step: WorkflowStep, idx: number) => {
      const prevStep = workflow.steps[idx - 1];
      const prevStepLive = prevStep?.live;
      const isCurrent = step.name === status;
      return Boolean(!isCurrent && (step.live || prevStepLive));
    });
  }
);
